import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS } from './utils/adminPermissions';

const BACKEND_URL = API_URL;

const AdminPlatformSettings = () => {
  const navigate = useNavigate();

  // Settings state
  const [verificationPublicAccess, setVerificationPublicAccess] = useState(true);
  const [holdFeatureEnabled, setHoldFeatureEnabled] = useState(true);
  const [recruiterPacketEnabled, setRecruiterPacketEnabled] = useState(true);
  const [matchingCandidatesAnonymized, setMatchingCandidatesAnonymized] = useState(true);
  const [raceRequired, setRaceRequired] = useState(true);
  const [ethnicityRequired, setEthnicityRequired] = useState(true);
  const [partnerVerificationRaceRequired, setPartnerVerificationRaceRequired] = useState(false);
  const [nacmeAdminWriteAccess, setNacmeAdminWriteAccess] = useState(false);
  const [showStatusField, setShowStatusField] = useState(true);
  const [showEligibleField, setShowEligibleField] = useState(true);
  const [grantCostsVisible, setGrantCostsVisible] = useState(false);

  // Chat moderation settings state - Channel Creation
  const [chatCreatePublicAlumni, setChatCreatePublicAlumni] = useState(true);
  const [chatCreatePrivateAlumni, setChatCreatePrivateAlumni] = useState(true);
  const [chatCreatePublicStudents, setChatCreatePublicStudents] = useState(true);
  const [chatCreatePrivateStudents, setChatCreatePrivateStudents] = useState(true);
  const [chatCreatePublicRecruiters, setChatCreatePublicRecruiters] = useState(true);
  const [chatCreatePrivateRecruiters, setChatCreatePrivateRecruiters] = useState(true);

  // Chat moderation settings state - DM Permissions (who can message whom)
  const [chatDmAlumniAlumni, setChatDmAlumniAlumni] = useState(true);
  const [chatDmAlumniStudents, setChatDmAlumniStudents] = useState(true);
  const [chatDmAlumniRecruiters, setChatDmAlumniRecruiters] = useState(true);
  const [chatDmStudentsStudents, setChatDmStudentsStudents] = useState(true);
  const [chatDmStudentsRecruiters, setChatDmStudentsRecruiters] = useState(true);
  const [chatDmRecruitersRecruiters, setChatDmRecruitersRecruiters] = useState(true);

  // Video conferencing settings
  const [videoConferencingEnabled, setVideoConferencingEnabled] = useState(true);

  // LinkedIn Comments settings
  const [linkedinCommentsEnabled, setLinkedinCommentsEnabled] = useState(true);
  const [linkedinCommentsBudget, setLinkedinCommentsBudget] = useState('50.00');
  const [linkedinCommentsMaxPages, setLinkedinCommentsMaxPages] = useState('1');
  const [linkedinCommentsSaving, setLinkedinCommentsSaving] = useState(false);

  // Versium/DataFinder Predictive Scores settings
  const [versiumEnabled, setVersiumEnabled] = useState(false);
  const [versiumApiKey, setVersiumApiKey] = useState('');
  const [versiumSaving, setVersiumSaving] = useState(false);

  // SWEE (Strategic Workforce Evolution Engine) settings
  const [sweeEnabled, setSweeEnabled] = useState(false);
  const [sweeDailyScanEnabled, setSweeDailyScanEnabled] = useState(false);
  const [sweeApiKey, setSweeApiKey] = useState('');
  const [sweeFmpApiKey, setSweeFmpApiKey] = useState('');
  const [sweeSimilarityThreshold, setSweeSimilarityThreshold] = useState('0.30');
  const [wiEnabled, setWiEnabled] = useState(false);
  const [wiSimilarityThreshold, setWiSimilarityThreshold] = useState('0.55');
  const [sweeSaving, setSweeSaving] = useState(false);
  const [theirStackApiKey, setTheirStackApiKey] = useState('');
  const [theirStackEnabled, setTheirStackEnabled] = useState(false);
  // External Signals settings
  const [warnApiKey, setWarnApiKey] = useState('');
  const [warnEnabled, setWarnEnabled] = useState(false);
  const [secEdgarEnabled, setSecEdgarEnabled] = useState(false);
  const [secEdgarUserAgent, setSecEdgarUserAgent] = useState('');
  const [fmpNewsPipelineEnabled, setFmpNewsPipelineEnabled] = useState(false);
  // Layoffs.fyi settings
  const [layoffsFyiEnabled, setLayoffsFyiEnabled] = useState(false);
  const [layoffsFyiLookbackDays, setLayoffsFyiLookbackDays] = useState('14');
  const [layoffsFyiIncludeInternational, setLayoffsFyiIncludeInternational] = useState(false);
  const [layoffsFyiInternationalWeight, setLayoffsFyiInternationalWeight] = useState('0.5');
  // LinkedIn Insights settings
  const [linkedinInsightsEnabled, setLinkedinInsightsEnabled] = useState(false);
  const [linkedinInsightsMonthlyRefresh, setLinkedinInsightsMonthlyRefresh] = useState(false);

  // Internship Matching & Student Resumes settings
  const [internshipMatchingEnabled, setInternshipMatchingEnabled] = useState(false);
  const [studentResumeUploadEnabled, setStudentResumeUploadEnabled] = useState(false);
  const [internshipApiKey, setInternshipApiKey] = useState('');
  const [internshipSimilarityThreshold, setInternshipSimilarityThreshold] = useState('0.30');
  const [internshipSaving, setInternshipSaving] = useState(false);
  const [internshipStats, setInternshipStats] = useState(null);
  const [internshipFetching, setInternshipFetching] = useState(false);
  const [internshipBackfilling, setInternshipBackfilling] = useState(false);
  const [internshipMatching, setInternshipMatching] = useState(false);

  // Auto-Process LinkedIn Profiles setting
  const [autoProcessLinkedIn, setAutoProcessLinkedIn] = useState(false);

  // Membership Management state
  const [membershipTiersEnabled, setMembershipTiersEnabled] = useState(false);
  const [membershipTiers, setMembershipTiers] = useState([]);
  const [membershipTiersLoading, setMembershipTiersLoading] = useState(false);
  const [featureGates, setFeatureGates] = useState([]);
  const [featureGatesLoading, setFeatureGatesLoading] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [editingGate, setEditingGate] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantSearch, setGrantSearch] = useState('');
  const [grantResults, setGrantResults] = useState([]);
  const [grantSelectedUser, setGrantSelectedUser] = useState(null);
  const [grantSelectedTier, setGrantSelectedTier] = useState('');
  const [grantStatus, setGrantStatus] = useState('comped');
  const [grantExpiresAt, setGrantExpiresAt] = useState('');
  const [grantCohortYear, setGrantCohortYear] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipsPage, setMembershipsPage] = useState(1);
  const [membershipsTotal, setMembershipsTotal] = useState(0);
  const [showMembershipSection, setShowMembershipSection] = useState(false);

  // UI state
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsModalData, setSettingsModalData] = useState(null);

  // Fetch all settings on mount
  useEffect(() => {
    fetchVerificationAccessSetting();
    fetchHoldFeatureSetting();
    fetchMatchingCandidatesAnonymizedSetting();
    fetchFormFieldSettings();
    fetchPartnerVerificationRaceSetting();
    fetchNacmeAdminWriteAccessSetting();
    fetchRecruiterDashboardSettings();
    fetchChatModerationSettings();
    fetchVideoConferencingSetting();
    fetchLinkedInCommentsSettings();
    fetchVersiumSettings();
    fetchSweeSettings();
    fetchAutoProcessSetting();
    fetchInternshipSettings();
    fetchMembershipTiersEnabled();
    fetchGrantCostsVisibleSetting();
    fetchRecruiterPacketSetting();
  }, []);

  // Fetch verification access setting
  const fetchVerificationAccessSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/partner_verification_public_access`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const settingValue = response.data.setting_value === 'true';
      setVerificationPublicAccess(settingValue);

    } catch (err) {
      console.error('Error fetching verification access setting:', err);
      // If error, keep default value of true
    }
  };

  // Fetch hold feature setting
  const fetchHoldFeatureSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/hold_feature_enabled`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const settingValue = response.data.setting_value === 'true';
      setHoldFeatureEnabled(settingValue);
    } catch (err) {
      console.error('Error fetching hold feature setting:', err);
      // If error, keep default value of false
    }
  };

  // Fetch form field settings
  const fetchFormFieldSettings = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      // Fetch race requirement setting
      const raceResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/race_field_required`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setRaceRequired(raceResponse.data.setting_value === 'true');

      // Fetch ethnicity requirement setting
      const ethnicityResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/ethnicity_field_required`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setEthnicityRequired(ethnicityResponse.data.setting_value === 'true');

    } catch (err) {
      console.error('Error fetching form field settings:', err);
      // Keep default values on error
    }
  };

  // Toggle verification public access
  const toggleVerificationPublicAccess = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !verificationPublicAccess;
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/settings/partner_verification_public_access`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setVerificationPublicAccess(newValue);

      // Show modal instead of error message
      setSettingsModalData({
        success: true,
        setting: 'verification',
        enabled: newValue,
        message: `Verification form access has been ${newValue ? 'enabled' : 'disabled'} successfully.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling verification access:', err);
      setSettingsModalData({
        success: false,
        setting: 'verification',
        message: 'Failed to update verification access setting.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Toggle hold feature
  const toggleHoldFeature = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !holdFeatureEnabled;
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/settings/hold_feature_enabled`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setHoldFeatureEnabled(newValue);

      setSettingsModalData({
        success: true,
        setting: 'hold',
        enabled: newValue,
        message: `Hold feature has been ${newValue ? 'enabled' : 'disabled'} successfully.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling hold feature:', err);
      setSettingsModalData({
        success: false,
        setting: 'hold',
        message: 'Failed to update hold feature setting.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch recruiter resume-packet export setting (default ON)
  const fetchRecruiterPacketSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/recruiter_packet_export_enabled`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Default ON: only disabled when explicitly set to 'false'
      setRecruiterPacketEnabled(response.data.setting_value !== 'false');
    } catch (err) {
      console.error('Error fetching recruiter packet setting:', err);
      setRecruiterPacketEnabled(true); // default ON when the setting is absent
    }
  };

  // Toggle recruiter resume-packet export
  const toggleRecruiterPacket = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !recruiterPacketEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/recruiter_packet_export_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setRecruiterPacketEnabled(newValue);
      setSettingsModalData({
        success: true,
        setting: 'recruiter_packet',
        enabled: newValue,
        message: `Recruiter resume packet export has been ${newValue ? 'enabled' : 'disabled'} successfully.`
      });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling recruiter packet export:', err);
      setSettingsModalData({
        success: false,
        setting: 'recruiter_packet',
        message: 'Failed to update recruiter packet export setting.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch grant costs visible setting
  const fetchGrantCostsVisibleSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/grant_costs_visible`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setGrantCostsVisible(response.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching grant costs visible setting:', err);
    }
  };

  // Toggle grant costs visibility
  const toggleGrantCostsVisible = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !grantCostsVisible;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/grant_costs_visible`,
        { setting_value: newValue.toString(), setting_type: 'boolean' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setGrantCostsVisible(newValue);
      setSettingsModalData({
        success: true,
        setting: 'grant_costs',
        enabled: newValue,
        message: `Grant AI cost tracking is now ${newValue ? 'visible' : 'hidden'} for non-superadmin users.`
      });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling grant costs visible:', err);
      setSettingsModalData({
        success: false,
        setting: 'grant_costs',
        message: 'Failed to update grant costs visibility setting.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch matching candidates anonymized setting
  const fetchMatchingCandidatesAnonymizedSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/matching_candidates_anonymized`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const settingValue = response.data.setting_value === 'true';
      setMatchingCandidatesAnonymized(settingValue);
    } catch (err) {
      console.error('Error fetching matching candidates anonymized setting:', err);
      // If error, keep default value of true
    }
  };

  // Toggle matching candidates anonymized
  const toggleMatchingCandidatesAnonymized = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !matchingCandidatesAnonymized;
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/settings/matching_candidates_anonymized`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setMatchingCandidatesAnonymized(newValue);

      setSettingsModalData({
        success: true,
        setting: 'anonymization',
        enabled: newValue,
        message: `Matching Candidates anonymization has been ${newValue ? 'enabled' : 'disabled'} successfully.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling matching candidates anonymization:', err);
      setSettingsModalData({
        success: false,
        setting: 'anonymization',
        message: 'Failed to update matching candidates anonymization setting.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Toggle race required
  const toggleRaceRequired = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !raceRequired;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/race_field_required`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Controls whether race field is required in student application form'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setRaceRequired(newValue);

      setSettingsModalData({
        success: true,
        setting: 'race',
        enabled: newValue,
        message: `Race field is now ${newValue ? 'required' : 'optional'}.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling race requirement:', err);
      setSettingsModalData({
        success: false,
        setting: 'race',
        message: 'Failed to update race field requirement.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Toggle ethnicity required
  const toggleEthnicityRequired = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !ethnicityRequired;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/ethnicity_field_required`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Controls whether ethnicity field is required in student application form'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setEthnicityRequired(newValue);

      setSettingsModalData({
        success: true,
        setting: 'ethnicity',
        enabled: newValue,
        message: `Ethnicity field is now ${newValue ? 'required' : 'optional'}.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling ethnicity requirement:', err);
      setSettingsModalData({
        success: false,
        setting: 'ethnicity',
        message: 'Failed to update ethnicity field requirement.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch partner verification race setting
  const fetchPartnerVerificationRaceSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/partner_verification_race_required`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setPartnerVerificationRaceRequired(response.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching partner verification race setting:', err);
      // Keep default value of false on error (data minimization)
      setPartnerVerificationRaceRequired(false);
    }
  };

  // Toggle partner verification race required
  const togglePartnerVerificationRaceRequired = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !partnerVerificationRaceRequired;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/partner_verification_race_required`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Controls whether race field is required in partner verification forms and CSV uploads'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setPartnerVerificationRaceRequired(newValue);

      setSettingsModalData({
        success: true,
        setting: 'partner_race',
        enabled: newValue,
        message: `Partner verification Race field is now ${newValue ? 'required' : 'optional'}.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling partner verification race requirement:', err);
      setSettingsModalData({
        success: false,
        setting: 'partner_race',
        message: 'Failed to update partner verification race field requirement.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch NACME Admin write access setting
  const fetchNacmeAdminWriteAccessSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/nacme_admin_write_access`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setNacmeAdminWriteAccess(response.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching NACME admin write access setting:', err);
      // Keep default value of false on error
    }
  };

  // Fetch recruiter dashboard field visibility settings
  const fetchRecruiterDashboardSettings = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const statusResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/recruiter_dashboard_show_status`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setShowStatusField(statusResponse.data.setting_value === 'true');

      const eligibleResponse = await axios.get(
        `${BACKEND_URL}/api/admin/settings/recruiter_dashboard_show_eligible`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setShowEligibleField(eligibleResponse.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching recruiter dashboard settings:', err);
      // Keep default values on error
    }
  };

  // Fetch chat moderation settings
  const fetchChatModerationSettings = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const settings = [
        // Channel creation permissions
        { key: 'chat_create_public_alumni', setter: setChatCreatePublicAlumni },
        { key: 'chat_create_private_alumni', setter: setChatCreatePrivateAlumni },
        { key: 'chat_create_public_students', setter: setChatCreatePublicStudents },
        { key: 'chat_create_private_students', setter: setChatCreatePrivateStudents },
        { key: 'chat_create_public_recruiters', setter: setChatCreatePublicRecruiters },
        { key: 'chat_create_private_recruiters', setter: setChatCreatePrivateRecruiters },
        // DM permissions (who can message whom)
        { key: 'chat_dm_alumni_alumni', setter: setChatDmAlumniAlumni },
        { key: 'chat_dm_alumni_students', setter: setChatDmAlumniStudents },
        { key: 'chat_dm_alumni_recruiters', setter: setChatDmAlumniRecruiters },
        { key: 'chat_dm_students_students', setter: setChatDmStudentsStudents },
        { key: 'chat_dm_students_recruiters', setter: setChatDmStudentsRecruiters },
        { key: 'chat_dm_recruiters_recruiters', setter: setChatDmRecruitersRecruiters },
      ];

      for (const { key, setter } of settings) {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/admin/settings/${key}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          setter(response.data.setting_value === 'true');
        } catch (err) {
          // Setting doesn't exist yet, keep default (true)
          console.log(`Setting ${key} not found, using default`);
        }
      }
    } catch (err) {
      console.error('Error fetching chat moderation settings:', err);
    }
  };

  // Fetch video conferencing setting
  const fetchVideoConferencingSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings/video_enabled`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setVideoConferencingEnabled(response.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching video conferencing setting:', err);
      // If error, keep default value of true
    }
  };

  // Toggle video conferencing
  const toggleVideoConferencing = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const newValue = !videoConferencingEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/video_enabled`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Enable/disable video conferencing feature in chat'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setVideoConferencingEnabled(newValue);

      setSettingsModalData({
        success: true,
        setting: 'video_enabled',
        enabled: newValue,
        message: `Video conferencing is now ${newValue ? 'enabled' : 'disabled'}.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling video conferencing:', err);
      setError('Failed to update video conferencing setting. Please try again.');
    } finally {
      setSettingsLoading(false);
    }
  };

  // ============================================
  // LinkedIn Comments Settings Functions
  // ============================================

  const fetchLinkedInCommentsSettings = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      const headers = { headers: { 'Authorization': `Bearer ${token}` } };
      const [enabledRes, budgetRes, pagesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/settings/linkedin_comments_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/linkedin_comments_monthly_budget_usd`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/linkedin_comments_max_pages_per_profile`, headers).catch(() => null),
      ]);
      if (enabledRes?.data) setLinkedinCommentsEnabled(enabledRes.data.setting_value === 'true');
      if (budgetRes?.data) setLinkedinCommentsBudget(budgetRes.data.setting_value || '50.00');
      if (pagesRes?.data) setLinkedinCommentsMaxPages(pagesRes.data.setting_value || '1');
    } catch (err) {
      console.error('Error fetching LinkedIn comments settings:', err);
    }
  };

  const toggleLinkedInCommentsEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !linkedinCommentsEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/linkedin_comments_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Enable/disable LinkedIn comment imports' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setLinkedinCommentsEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'linkedin_comments_enabled', enabled: newValue, message: `LinkedIn comment imports are now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling LinkedIn comments:', err);
      setError('Failed to update LinkedIn comments setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveLinkedInCommentsSetting = async (key, value, type, description) => {
    setLinkedinCommentsSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/${key}`,
        { setting_value: value, setting_type: type, description },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: key, message: `Setting updated successfully.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error(`Error saving ${key}:`, err);
      setError(`Failed to update ${key}.`);
    } finally {
      setLinkedinCommentsSaving(false);
    }
  };

  // ============================================
  // Versium/DataFinder Predictive Scores Functions
  // ============================================

  const fetchVersiumSettings = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      const headers = { headers: { 'Authorization': `Bearer ${token}` } };
      const [enabledRes, keyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/settings/versium_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/versium_api_key`, headers).catch(() => null),
      ]);
      if (enabledRes?.data) setVersiumEnabled(enabledRes.data.setting_value === 'true');
      if (keyRes?.data) setVersiumApiKey(keyRes.data.setting_value || '');
    } catch (err) {
      console.error('Error fetching Versium settings:', err);
    }
  };

  const toggleVersiumEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !versiumEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/versium_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Enable/disable Versium predictive scores feature' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setVersiumEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'versium_enabled', enabled: newValue, message: `Versium predictive scores are now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling Versium:', err);
      setError('Failed to update Versium setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveVersiumApiKey = async () => {
    setVersiumSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/versium_api_key`,
        { setting_value: versiumApiKey, setting_type: 'string', description: 'Versium/DataFinder API key for predictive scores' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'versium_api_key', message: 'Versium API key saved successfully.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving Versium API key:', err);
      setError('Failed to save Versium API key.');
    } finally {
      setVersiumSaving(false);
    }
  };

  // SWEE (Strategic Workforce Evolution Engine) Functions
  const fetchSweeSettings = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      const headers = { headers: { 'Authorization': `Bearer ${token}` } };
      const [enabledRes, scanRes, keyRes, fmpKeyRes, threshRes, wiEnabledRes, wiThreshRes, tsKeyRes, tsEnabledRes,
             warnKeyRes, warnEnabledRes, secEdgarEnabledRes, secEdgarUaRes, fmpNewsEnabledRes,
             lfyiEnabledRes, lfyiLookbackRes, lfyiIntlRes, lfyiIntlWeightRes,
             liInsightsEnabledRes, liInsightsMonthlyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/settings/swee_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/swee_daily_scan_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/earningscall_api_key`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/fmp_api_key`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/swee_similarity_threshold`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/wi_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/wi_similarity_threshold`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/theirstack_api_key`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/theirstack_pipeline_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/warn_firehose_api_key`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/warn_pipeline_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/sec_edgar_pipeline_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/sec_edgar_user_agent`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/fmp_news_pipeline_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/layoffs_fyi_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/layoffs_fyi_lookback_days`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/layoffs_fyi_include_international`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/layoffs_fyi_international_weight`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/linkedin_insights_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/linkedin_insights_monthly_refresh`, headers).catch(() => null),
      ]);
      if (enabledRes?.data) setSweeEnabled(enabledRes.data.setting_value === 'true');
      if (scanRes?.data) setSweeDailyScanEnabled(scanRes.data.setting_value === 'true');
      if (keyRes?.data) setSweeApiKey(keyRes.data.setting_value || '');
      if (fmpKeyRes?.data) setSweeFmpApiKey(fmpKeyRes.data.setting_value || '');
      if (threshRes?.data) setSweeSimilarityThreshold(threshRes.data.setting_value || '0.65');
      if (wiEnabledRes?.data) setWiEnabled(wiEnabledRes.data.setting_value === 'true');
      if (wiThreshRes?.data) setWiSimilarityThreshold(wiThreshRes.data.setting_value || '0.55');
      if (tsKeyRes?.data) setTheirStackApiKey(tsKeyRes.data.setting_value || '');
      if (tsEnabledRes?.data) setTheirStackEnabled(tsEnabledRes.data.setting_value === 'true');
      if (warnKeyRes?.data) setWarnApiKey(warnKeyRes.data.setting_value || '');
      if (warnEnabledRes?.data) setWarnEnabled(warnEnabledRes.data.setting_value === 'true');
      if (secEdgarEnabledRes?.data) setSecEdgarEnabled(secEdgarEnabledRes.data.setting_value === 'true');
      if (secEdgarUaRes?.data) setSecEdgarUserAgent(secEdgarUaRes.data.setting_value || '');
      if (fmpNewsEnabledRes?.data) setFmpNewsPipelineEnabled(fmpNewsEnabledRes.data.setting_value === 'true');
      if (lfyiEnabledRes?.data) setLayoffsFyiEnabled(lfyiEnabledRes.data.setting_value === 'true');
      if (lfyiLookbackRes?.data) setLayoffsFyiLookbackDays(lfyiLookbackRes.data.setting_value || '14');
      if (lfyiIntlRes?.data) setLayoffsFyiIncludeInternational(lfyiIntlRes.data.setting_value === 'true');
      if (lfyiIntlWeightRes?.data) setLayoffsFyiInternationalWeight(lfyiIntlWeightRes.data.setting_value || '0.5');
      if (liInsightsEnabledRes?.data) setLinkedinInsightsEnabled(liInsightsEnabledRes.data.setting_value === 'true');
      if (liInsightsMonthlyRes?.data) setLinkedinInsightsMonthlyRefresh(liInsightsMonthlyRes.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching SWEE settings:', err);
    }
  };

  // Internship Matching settings functions
  const fetchInternshipSettings = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      const headers = { headers: { 'Authorization': `Bearer ${token}` } };
      const [matchRes, uploadRes, keyRes, threshRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/settings/internship_matching_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/student_resume_upload_enabled`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/internship_api_key`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/settings/internship_similarity_threshold`, headers).catch(() => null),
        axios.get(`${BACKEND_URL}/api/admin/internships/stats`, headers).catch(() => null),
      ]);
      if (matchRes?.data) setInternshipMatchingEnabled(matchRes.data.setting_value === 'true');
      if (uploadRes?.data) setStudentResumeUploadEnabled(uploadRes.data.setting_value === 'true');
      if (keyRes?.data) setInternshipApiKey(keyRes.data.setting_value || '');
      if (threshRes?.data) setInternshipSimilarityThreshold(threshRes.data.setting_value || '0.30');
      if (statsRes?.data) setInternshipStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching internship settings:', err);
    }
  };

  const toggleInternshipMatching = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !internshipMatchingEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/internship_matching_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Enable/disable internship matching' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setInternshipMatchingEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'internship_matching_enabled', enabled: newValue, message: `Internship matching is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling internship matching:', err);
      setError('Failed to update internship matching setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleStudentResumeUpload = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !studentResumeUploadEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/student_resume_upload_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Enable/disable student resume upload on dashboard' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setStudentResumeUploadEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'student_resume_upload_enabled', enabled: newValue, message: `Student resume upload is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling student resume upload:', err);
      setError('Failed to update resume upload setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveInternshipSettings = async () => {
    setInternshipSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); return; }
      const headers = { headers: { 'Authorization': `Bearer ${token}` } };
      await Promise.all([
        axios.put(`${BACKEND_URL}/api/admin/settings/internship_api_key`,
          { setting_value: internshipApiKey, setting_type: 'string', description: 'RapidAPI key for Internships API' }, headers),
        axios.put(`${BACKEND_URL}/api/admin/settings/internship_similarity_threshold`,
          { setting_value: internshipSimilarityThreshold, setting_type: 'string', description: 'Minimum cosine similarity for internship matches' }, headers),
      ]);
      setSettingsModalData({ success: true, message: 'Internship settings saved.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving internship settings:', err);
      setError('Failed to save internship settings.');
    } finally {
      setInternshipSaving(false);
    }
  };

  const triggerInternshipFetch = async () => {
    setInternshipFetching(true);
    try {
      const token = getValidToken();
      if (!token) return;
      await axios.post(`${BACKEND_URL}/api/admin/internships/fetch`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSettingsModalData({ success: true, message: 'Internship fetch started in background. Check stats in a few minutes.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error triggering internship fetch:', err);
      setError('Failed to trigger internship fetch.');
    } finally {
      setInternshipFetching(false);
    }
  };

  const triggerResumeBackfill = async () => {
    setInternshipBackfilling(true);
    try {
      const token = getValidToken();
      if (!token) return;
      await axios.post(`${BACKEND_URL}/api/admin/internships/backfill-resumes`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSettingsModalData({ success: true, message: 'Resume backfill started in background. This may take ~17 minutes for ~210 resumes.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error triggering resume backfill:', err);
      setError('Failed to trigger resume backfill.');
    } finally {
      setInternshipBackfilling(false);
    }
  };

  const triggerComputeMatches = async () => {
    setInternshipMatching(true);
    try {
      const token = getValidToken();
      if (!token) return;
      await axios.post(`${BACKEND_URL}/api/admin/internships/compute-matches`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSettingsModalData({ success: true, message: 'Match computation started in background. Check stats shortly for results.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error triggering match computation:', err);
      setError('Failed to trigger match computation.');
    } finally {
      setInternshipMatching(false);
    }
  };

  const toggleSweeEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !sweeEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/swee_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Enable/disable SWEE workforce evolution engine' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSweeEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'swee_enabled', enabled: newValue, message: `SWEE is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling SWEE:', err);
      setError('Failed to update SWEE setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleSweeDailyScan = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !sweeDailyScanEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/swee_daily_scan_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Enable/disable SWEE daily transcript scan' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSweeDailyScanEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'swee_daily_scan_enabled', enabled: newValue, message: `Daily transcript scan is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling SWEE daily scan:', err);
      setError('Failed to update daily scan setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleWiEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !wiEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/wi_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Enable/disable Workforce Intelligence module' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setWiEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'wi_enabled', enabled: newValue, message: `Workforce Intelligence is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling WI:', err);
      setError('Failed to update WI setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveWiSimilarityThreshold = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const val = parseFloat(wiSimilarityThreshold);
      if (isNaN(val) || val < 0.1 || val > 1.0) {
        setError('WI threshold must be between 0.1 and 1.0');
        setSweeSaving(false);
        return;
      }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/wi_similarity_threshold`,
        { setting_value: val.toString(), setting_type: 'float', description: 'WI cosine similarity gate threshold' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'wi_similarity_threshold', message: `WI threshold updated to ${val}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving WI threshold:', err);
      setError('Failed to save WI threshold.');
    } finally {
      setSweeSaving(false);
    }
  };

  const toggleFmpNewsPipeline = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !fmpNewsPipelineEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/fmp_news_pipeline_enabled`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Enable/disable FMP News & Press Release pipeline' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setFmpNewsPipelineEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'fmp_news_pipeline_enabled', enabled: newValue, message: `FMP News Pipeline is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling FMP News Pipeline:', err);
      setError('Failed to update FMP News Pipeline setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleLayoffsFyiEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !layoffsFyiEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/layoffs_fyi_enabled`,
        { setting_value: String(newValue), setting_type: 'boolean', description: 'Enable/disable Layoffs.fyi Airtable scraping pipeline' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setLayoffsFyiEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'layoffs_fyi_enabled', enabled: newValue, message: `Layoffs.fyi pipeline is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling Layoffs.fyi:', err);
      setError('Failed to toggle Layoffs.fyi pipeline.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleLayoffsFyiInternational = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !layoffsFyiIncludeInternational;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/layoffs_fyi_include_international`,
        { setting_value: String(newValue), setting_type: 'boolean', description: 'Include international layoff signals from Layoffs.fyi' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setLayoffsFyiIncludeInternational(newValue);
    } catch (err) {
      console.error('Error toggling Layoffs.fyi international:', err);
      setError('Failed to toggle international setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveLayoffsFyiLookbackDays = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const val = parseInt(layoffsFyiLookbackDays);
      if (isNaN(val) || val < 1 || val > 365) {
        setError('Lookback days must be between 1 and 365');
        setSweeSaving(false);
        return;
      }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/layoffs_fyi_lookback_days`,
        { setting_value: val.toString(), setting_type: 'integer', description: 'How many days back to scan Layoffs.fyi data' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'layoffs_fyi_lookback_days', message: `Lookback updated to ${val} days.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving lookback days:', err);
      setError('Failed to save lookback days.');
    } finally {
      setSweeSaving(false);
    }
  };

  const saveLayoffsFyiInternationalWeight = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const val = parseFloat(layoffsFyiInternationalWeight);
      if (isNaN(val) || val < 0.0 || val > 1.0) {
        setError('International weight must be between 0.0 and 1.0');
        setSweeSaving(false);
        return;
      }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/layoffs_fyi_international_weight`,
        { setting_value: val.toString(), setting_type: 'float', description: 'Weight multiplier for international layoff signals (0.0-1.0)' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'layoffs_fyi_international_weight', message: `International weight updated to ${val}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving international weight:', err);
      setError('Failed to save international weight.');
    } finally {
      setSweeSaving(false);
    }
  };

  const toggleLinkedinInsightsEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !linkedinInsightsEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/linkedin_insights_enabled`,
        { setting_value: String(newValue), setting_type: 'boolean', description: 'Enable/disable LinkedIn Company Insights pipeline (RICEMAN Apify actor)' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setLinkedinInsightsEnabled(newValue);
      setSettingsModalData({ success: true, setting: 'linkedin_insights_enabled', enabled: newValue, message: `LinkedIn Insights pipeline is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling LinkedIn Insights:', err);
      setError('Failed to toggle LinkedIn Insights pipeline.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleLinkedinInsightsMonthlyRefresh = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !linkedinInsightsMonthlyRefresh;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/linkedin_insights_monthly_refresh`,
        { setting_value: String(newValue), setting_type: 'boolean', description: 'Enable/disable automatic monthly refresh of LinkedIn insights for tracked companies' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setLinkedinInsightsMonthlyRefresh(newValue);
      setSettingsModalData({ success: true, setting: 'linkedin_insights_monthly_refresh', enabled: newValue, message: `Monthly refresh is now ${newValue ? 'enabled' : 'disabled'}. ${newValue ? 'Tracked companies will be refreshed monthly (~$0.19/company).' : ''}` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling LinkedIn Insights monthly refresh:', err);
      setError('Failed to toggle monthly refresh.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSweeApiKey = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/earningscall_api_key`,
        { setting_value: sweeApiKey, setting_type: 'string', description: 'EarningsCall.biz API key for transcript retrieval' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'earningscall_api_key', message: 'EarningsCall API key saved successfully.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving SWEE API key:', err);
      setError('Failed to save EarningsCall API key.');
    } finally {
      setSweeSaving(false);
    }
  };

  const saveFmpApiKey = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/fmp_api_key`,
        { setting_value: sweeFmpApiKey, setting_type: 'string', description: 'Financial Modeling Prep API key for employee count data' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'fmp_api_key', message: 'FMP API key saved successfully.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving FMP API key:', err);
      setError('Failed to save FMP API key.');
    } finally {
      setSweeSaving(false);
    }
  };

  const saveTheirStackApiKey = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/theirstack_api_key`,
        { setting_value: theirStackApiKey, setting_type: 'string', description: 'TheirStack API key for job posting data ($59/mo plan)' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'theirstack_api_key', message: 'TheirStack API key saved successfully.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving TheirStack API key:', err);
      setError('Failed to save TheirStack API key.');
    } finally {
      setSweeSaving(false);
    }
  };

  const toggleTheirStackEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !theirStackEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/theirstack_pipeline_enabled`,
        { setting_value: String(newValue), setting_type: 'boolean', description: 'Enable/disable monthly TheirStack snapshot collection' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setTheirStackEnabled(newValue);
    } catch (err) {
      console.error('Error toggling TheirStack:', err);
      setError('Failed to toggle TheirStack pipeline.');
    } finally {
      setSettingsLoading(false);
    }
  };

  // External Signals Functions
  const toggleWarnEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !warnEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/warn_pipeline_enabled`,
        { setting_value: String(newValue), setting_type: 'boolean', description: 'Enable/disable WARN Act daily scan pipeline' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setWarnEnabled(newValue);
    } catch (err) {
      console.error('Error toggling WARN:', err);
      setError('Failed to toggle WARN pipeline.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveWarnApiKey = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/warn_firehose_api_key`,
        { setting_value: warnApiKey, setting_type: 'string', description: 'WARN Firehose API key' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'warn_firehose_api_key', message: 'WARN API key saved.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving WARN key:', err);
      setError('Failed to save WARN API key.');
    } finally {
      setSweeSaving(false);
    }
  };

  const toggleSecEdgarEnabled = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !secEdgarEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/sec_edgar_pipeline_enabled`,
        { setting_value: String(newValue), setting_type: 'boolean', description: 'Enable/disable SEC EDGAR 8-K daily scan pipeline' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSecEdgarEnabled(newValue);
    } catch (err) {
      console.error('Error toggling SEC EDGAR:', err);
      setError('Failed to toggle SEC EDGAR pipeline.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSecEdgarUserAgent = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/sec_edgar_user_agent`,
        { setting_value: secEdgarUserAgent, setting_type: 'string', description: 'SEC EDGAR User-Agent header (required by SEC)' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'sec_edgar_user_agent', message: 'SEC EDGAR User-Agent saved.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving SEC EDGAR user agent:', err);
      setError('Failed to save SEC EDGAR User-Agent.');
    } finally {
      setSweeSaving(false);
    }
  };

  const saveSweeSimilarityThreshold = async () => {
    setSweeSaving(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const val = parseFloat(sweeSimilarityThreshold);
      if (isNaN(val) || val < 0.1 || val > 1.0) {
        setError('Threshold must be between 0.1 and 1.0');
        setSweeSaving(false);
        return;
      }
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/swee_similarity_threshold`,
        { setting_value: val.toFixed(2), setting_type: 'string', description: 'Cosine similarity threshold for anchor matching' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSettingsModalData({ success: true, setting: 'swee_similarity_threshold', message: `Similarity threshold set to ${val.toFixed(2)}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error saving SWEE threshold:', err);
      setError('Failed to save similarity threshold.');
    } finally {
      setSweeSaving(false);
    }
  };

  // Auto-Process LinkedIn Profiles Functions
  const fetchAutoProcessSetting = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/settings/auto_process_linkedin_profiles`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      ).catch(() => null);
      if (res?.data) setAutoProcessLinkedIn(res.data.setting_value === 'true');
    } catch (err) {
      console.error('Error fetching auto-process setting:', err);
    }
  };

  const toggleAutoProcessLinkedIn = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) { setError('Session expired.'); setTimeout(() => navigate('/'), 2000); return; }
      const newValue = !autoProcessLinkedIn;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/auto_process_linkedin_profiles`,
        { setting_value: newValue.toString(), setting_type: 'boolean', description: 'Auto-process LinkedIn profiles when URL is saved on unprovisioned contacts' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setAutoProcessLinkedIn(newValue);
      setSettingsModalData({ success: true, setting: 'auto_process_linkedin_profiles', enabled: newValue, message: `Auto-processing of LinkedIn profiles is now ${newValue ? 'enabled' : 'disabled'}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling auto-process:', err);
      setError('Failed to update auto-process setting.');
    } finally {
      setSettingsLoading(false);
    }
  };

  // ============================================
  // Membership Management Functions
  // ============================================

  const fetchMembershipTiersEnabled = async () => {
    try {
      const token = getValidToken();
      if (!token) return;
      const response = await axios.get(
        `${BACKEND_URL}/api/feature-toggles/public`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      // Check membership_tiers_enabled - may not be in public list, try admin endpoint
      const adminResponse = await axios.get(
        `${BACKEND_URL}/api/admin/feature-toggles`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const toggle = adminResponse.data.find(t => t.feature_name === 'membership_tiers_enabled');
      if (toggle) setMembershipTiersEnabled(toggle.is_enabled);
    } catch (err) {
      console.error('Error fetching membership tiers toggle:', err);
    }
  };

  const toggleMembershipTiers = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;
      const newValue = !membershipTiersEnabled;
      await axios.put(
        `${BACKEND_URL}/api/admin/feature-toggles/membership_tiers_enabled`,
        { is_enabled: newValue },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setMembershipTiersEnabled(newValue);
      setSettingsModalData({
        success: true,
        message: `Membership tier system is now ${newValue ? 'ENABLED - tier checks enforced for brokerage requests' : 'DISABLED - all brokerage requests bypass tier checks'}.`
      });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error toggling membership tiers:', err);
      setSettingsModalData({ success: false, message: 'Failed to toggle membership tiers.' });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchMembershipTiers = async () => {
    setMembershipTiersLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/membership/tiers`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setMembershipTiers(response.data);
    } catch (err) {
      console.error('Error fetching membership tiers:', err);
    } finally {
      setMembershipTiersLoading(false);
    }
  };

  const fetchFeatureGates = async () => {
    setFeatureGatesLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/membership/feature-gates`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setFeatureGates(response.data);
    } catch (err) {
      console.error('Error fetching feature gates:', err);
    } finally {
      setFeatureGatesLoading(false);
    }
  };

  const fetchMemberships = async (page = 1) => {
    setMembershipsLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/membership/memberships?page=${page}&page_size=20`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setMemberships(response.data.items);
      setMembershipsTotal(response.data.total);
      setMembershipsPage(page);
    } catch (err) {
      console.error('Error fetching memberships:', err);
    } finally {
      setMembershipsLoading(false);
    }
  };

  const handleSaveTier = async (tierId, updates) => {
    try {
      const token = getValidToken();
      if (!token) return;
      await axios.put(
        `${BACKEND_URL}/api/admin/membership/tiers/${tierId}`,
        updates,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setEditingTier(null);
      fetchMembershipTiers();
      setSettingsModalData({ success: true, message: 'Tier updated successfully.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error updating tier:', err);
      setSettingsModalData({ success: false, message: 'Failed to update tier.' });
      setShowSettingsModal(true);
    }
  };

  const handleSaveGate = async (gateId, updates) => {
    try {
      const token = getValidToken();
      if (!token) return;
      await axios.put(
        `${BACKEND_URL}/api/admin/membership/feature-gates/${gateId}`,
        updates,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setEditingGate(null);
      fetchFeatureGates();
      setSettingsModalData({ success: true, message: 'Feature gate updated successfully.' });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error updating feature gate:', err);
      setSettingsModalData({ success: false, message: 'Failed to update feature gate.' });
      setShowSettingsModal(true);
    }
  };

  const handleSearchAlumni = async (query) => {
    if (!query || query.length < 2) { setGrantResults([]); return; }
    try {
      const token = getValidToken();
      if (!token) return;
      const response = await axios.get(
        `${BACKEND_URL}/api/alumni/directory/search?q=${encodeURIComponent(query)}&limit=10`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setGrantResults(response.data.alumni || []);
    } catch (err) {
      console.error('Error searching alumni:', err);
    }
  };

  const handleGrantMembership = async () => {
    if (!grantSelectedUser || !grantSelectedTier) return;
    setGrantLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;
      const body = {
        alumni_user_id: grantSelectedUser.id,
        tier_slug: grantSelectedTier,
        status: grantStatus,
      };
      if (grantExpiresAt) body.expires_at = new Date(grantExpiresAt).toISOString();
      if (grantCohortYear) body.cohort_year = parseInt(grantCohortYear);

      await axios.post(
        `${BACKEND_URL}/api/admin/membership/grant`,
        body,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setShowGrantModal(false);
      setGrantSelectedUser(null);
      setGrantSelectedTier('');
      setGrantSearch('');
      setGrantResults([]);
      fetchMemberships(membershipsPage);
      setSettingsModalData({ success: true, message: `Membership granted to ${grantSelectedUser.first_name} ${grantSelectedUser.last_name}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error granting membership:', err);
      setSettingsModalData({ success: false, message: err.response?.data?.detail || 'Failed to grant membership.' });
      setShowSettingsModal(true);
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokeMembership = async (membershipId, alumniName) => {
    if (!window.confirm(`Revoke membership for ${alumniName}?`)) return;
    try {
      const token = getValidToken();
      if (!token) return;
      // Find the membership to get alumni_user_id
      const membership = memberships.find(m => m.id === membershipId);
      if (!membership) return;
      await axios.post(
        `${BACKEND_URL}/api/admin/membership/grant`,
        { alumni_user_id: membership.alumni_user_id, tier_slug: membership.tier_slug, status: 'cancelled' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      fetchMemberships(membershipsPage);
      setSettingsModalData({ success: true, message: `Membership revoked for ${alumniName}.` });
      setShowSettingsModal(true);
    } catch (err) {
      console.error('Error revoking membership:', err);
      setSettingsModalData({ success: false, message: 'Failed to revoke membership.' });
      setShowSettingsModal(true);
    }
  };

  // Load membership data when section is expanded
  useEffect(() => {
    if (showMembershipSection) {
      fetchMembershipTiers();
      fetchFeatureGates();
      fetchMemberships(1);
    }
  }, [showMembershipSection]);

  // Generic toggle for chat settings
  const toggleChatSetting = async (settingKey, currentValue, setter, userGroup, channelType) => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !currentValue;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/${settingKey}`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: `Controls whether ${userGroup} can create ${channelType} channels`
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setter(newValue);

      setSettingsModalData({
        success: true,
        setting: settingKey,
        enabled: newValue,
        message: `${userGroup} can ${newValue ? 'now' : 'no longer'} create ${channelType} channels.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error(`Error toggling ${settingKey}:`, err);
      setSettingsModalData({
        success: false,
        setting: settingKey,
        message: `Failed to update ${userGroup} ${channelType} channel creation setting.`
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Toggle for DM permission settings
  const toggleDmPermission = async (settingKey, currentValue, setter, group1, group2) => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !currentValue;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/${settingKey}`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: `Controls whether ${group1} can send direct messages to ${group2}`
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setter(newValue);

      const groupLabel = group1 === group2 ? `${group1} (within group)` : `${group1} ↔ ${group2}`;
      setSettingsModalData({
        success: true,
        setting: settingKey,
        enabled: newValue,
        message: `Direct messaging ${newValue ? 'enabled' : 'disabled'} for ${groupLabel}.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error(`Error toggling ${settingKey}:`, err);
      setSettingsModalData({
        success: false,
        setting: settingKey,
        message: `Failed to update DM permission setting.`
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Toggle NACME Admin write access
  const toggleNacmeAdminWriteAccess = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !nacmeAdminWriteAccess;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/nacme_admin_write_access`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Controls whether admins can edit NACME Admin Record fields in student details'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setNacmeAdminWriteAccess(newValue);

      setSettingsModalData({
        success: true,
        setting: 'nacme_write',
        enabled: newValue,
        message: `NACME Admin Record write access has been ${newValue ? 'enabled' : 'disabled'}.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling NACME admin write access:', err);
      setSettingsModalData({
        success: false,
        setting: 'nacme_write',
        message: 'Failed to update NACME Admin write access setting.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Toggle Status field visibility
  const toggleStatusFieldVisibility = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !showStatusField;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/recruiter_dashboard_show_status`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Controls whether Status field is visible in recruiter dashboard student cards'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setShowStatusField(newValue);

      setSettingsModalData({
        success: true,
        setting: 'status_visibility',
        enabled: newValue,
        message: `Status field visibility has been ${newValue ? 'enabled' : 'disabled'} in recruiter dashboard.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling Status field visibility:', err);
      setSettingsModalData({
        success: false,
        setting: 'status_visibility',
        message: 'Failed to update Status field visibility setting.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Toggle Eligible field visibility
  const toggleEligibleFieldVisibility = async () => {
    setSettingsLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      const newValue = !showEligibleField;
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/recruiter_dashboard_show_eligible`,
        {
          setting_value: newValue.toString(),
          setting_type: 'boolean',
          description: 'Controls whether Eligible field is visible in recruiter dashboard student cards'
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setShowEligibleField(newValue);

      setSettingsModalData({
        success: true,
        setting: 'eligible_visibility',
        enabled: newValue,
        message: `Eligible field visibility has been ${newValue ? 'enabled' : 'disabled'} in recruiter dashboard.`
      });
      setShowSettingsModal(true);

    } catch (err) {
      console.error('Error toggling Eligible field visibility:', err);
      setSettingsModalData({
        success: false,
        setting: 'eligible_visibility',
        message: 'Failed to update Eligible field visibility setting.'
      });
      setShowSettingsModal(true);
    } finally {
      setSettingsLoading(false);
    }
  };

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.PLATFORM_SETTINGS}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Platform Settings
                </h1>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Manage platform-wide settings and feature toggles
                </p>
              </div>
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Back to Admin
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Settings Sections */}
          <div className="space-y-6">
            {/* Verification Access Control */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200">
                    Verification Form Public Access
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    Control whether verification forms are publicly accessible via direct links. When disabled, forms are only accessible through the partner dashboard.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleVerificationPublicAccess}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      verificationPublicAccess
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        verificationPublicAccess ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Hold Feature Control */}
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-purple-800 dark:text-purple-200">
                    Recruiter Hold Feature
                  </h3>
                  <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                    Allow recruiters to place holds on candidates. When enabled, recruiters can temporarily reserve students with a 7-day cooldown after release.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleHoldFeature}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                      holdFeatureEnabled
                        ? 'bg-purple-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        holdFeatureEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Recruiter Resume Packet Export Control */}
            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-teal-800 dark:text-teal-200">
                    Recruiter Resume Packet Export
                  </h3>
                  <p className="text-sm text-teal-700 dark:text-teal-300 mt-1">
                    Show the Shortlisted / Favorited / Applied packet export buttons on the recruiter interaction-history page. Each button compiles candidate resumes into a single PDF plus a CSV of student data. Enabled by default; turn off to hide the buttons.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleRecruiterPacket}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                      recruiterPacketEnabled
                        ? 'bg-teal-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        recruiterPacketEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Grant AI Costs Visibility Control */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-amber-800 dark:text-amber-200">
                    Grant AI Cost Tracking
                  </h3>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Show the Costs tab on the Grant Management page to non-superadmin users. Superadmins always have access regardless of this setting.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleGrantCostsVisible}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                      grantCostsVisible
                        ? 'bg-amber-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        grantCostsVisible ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Matching Candidates Anonymization Control */}
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-orange-800 dark:text-orange-200">
                    Matching Candidates Anonymization
                  </h3>
                  <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                    Control whether students in the "Matching Candidates" section are shown anonymously to recruiters. When enabled, students who match criteria but haven't applied yet are displayed as "Candidate #ID" without names, photos, or contact information.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleMatchingCandidatesAnonymized}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      matchingCandidatesAnonymized
                        ? 'bg-orange-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        matchingCandidatesAnonymized ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* NACME Admin Write Access Control */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-green-800 dark:text-green-200">
                    NACME Admin Record Write Access
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Enable write access to NACME Admin Record fields (scholarship disbursements, financial tracking). When enabled, admins can edit these fields directly from the student detail page.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleNacmeAdminWriteAccess}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      nacmeAdminWriteAccess
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        nacmeAdminWriteAccess ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Recruiter Dashboard Field Visibility Control */}
            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-teal-800 dark:text-teal-200 mb-4">
                Recruiter Dashboard Field Visibility
              </h3>
              <p className="text-sm text-teal-700 dark:text-teal-300 mb-6">
                Control which fields are visible in recruiter dashboard student cards.
              </p>

              {/* Status Field Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 pb-4 border-b border-teal-200 dark:border-teal-700">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-teal-800 dark:text-teal-200">
                    Show Status Field
                  </h4>
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                    Displays "Verified" or "Pending" verification status for students.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleStatusFieldVisibility}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                      showStatusField
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showStatusField ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Eligible Field Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-teal-800 dark:text-teal-200">
                    Show Eligible Field
                  </h4>
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                    Displays "Yes" or "Pending" eligibility status for students.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleEligibleFieldVisibility}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                      showEligibleField
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showEligibleField ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Form Field Requirements Control */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200 mb-4">
                Student Application Form Settings
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-6">
                Control which fields are required in the student application form.
              </p>

              {/* Race Field Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 pb-4 border-b border-blue-200 dark:border-blue-700">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Race Field Required
                  </h4>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Students must provide race information when {raceRequired ? 'enabled' : 'disabled'}.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleRaceRequired}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      raceRequired
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        raceRequired ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Ethnicity Field Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Ethnicity Field Required
                  </h4>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Students must provide ethnicity information when {ethnicityRequired ? 'enabled' : 'disabled'}.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={toggleEthnicityRequired}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      ethnicityRequired
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        ethnicityRequired ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Status Summary */}
              <div className="mt-6 pt-4 border-t border-blue-200 dark:border-blue-700">
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">Current Status:</p>
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    raceRequired
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}>
                    Race: {raceRequired ? 'Required' : 'Optional'}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    ethnicityRequired
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}>
                    Ethnicity: {ethnicityRequired ? 'Required' : 'Optional'}
                  </span>
                </div>
              </div>
            </div>

            {/* Partner Verification Form Settings */}
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-indigo-800 dark:text-indigo-200 mb-4">
                Partner Verification Form Settings
              </h3>
              <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-6">
                Control which fields are required in partner verification forms and CSV uploads.
              </p>

              {/* Race Field Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
                    Race Field Required
                  </h4>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                    When enabled, race field is required in verification forms and CSV uploads. When disabled, field is hidden and removed from templates.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={togglePartnerVerificationRaceRequired}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      partnerVerificationRaceRequired
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        partnerVerificationRaceRequired ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Status Summary */}
              <div className="mt-6 pt-4 border-t border-indigo-200 dark:border-indigo-700">
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-2">Current Status:</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  partnerVerificationRaceRequired
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  Race: {partnerVerificationRaceRequired ? 'Required' : 'Optional'}
                </span>
              </div>
            </div>

            {/* Video Conferencing Settings */}
            <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-violet-800 dark:text-violet-200 mb-2">
                Video Conferencing
              </h3>
              <p className="text-sm text-violet-700 dark:text-violet-300 mb-4">
                Control the video calling feature in the chat window. When disabled, users cannot initiate or receive video calls.
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-violet-800 dark:text-violet-200">Video Calls in Chat</span>
                    <p className="text-xs text-violet-600 dark:text-violet-400">Allow users to make video calls from DM conversations</p>
                  </div>
                </div>
                <button
                  onClick={toggleVideoConferencing}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                    videoConferencingEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    videoConferencingEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Status indicator */}
              <div className="mt-4 pt-4 border-t border-violet-200 dark:border-violet-700">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  videoConferencingEnabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  <span className={`w-2 h-2 rounded-full mr-2 ${videoConferencingEnabled ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  {videoConferencingEnabled ? 'Video Calls Enabled' : 'Video Calls Disabled'}
                </span>
              </div>
            </div>

            {/* Chat Moderation Settings */}
            <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-cyan-800 dark:text-cyan-200 mb-2">
                Chat Moderation Settings
              </h3>
              <p className="text-sm text-cyan-700 dark:text-cyan-300 mb-6">
                Control channel creation permissions for each user group. When disabled, users in that group cannot create channels of that type.
              </p>

              {/* Alumni Channel Creation */}
              <div className="mb-6 pb-4 border-b border-cyan-200 dark:border-cyan-700">
                <h4 className="text-sm font-semibold text-cyan-800 dark:text-cyan-200 mb-3 flex items-center">
                  <span className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs mr-2">A</span>
                  Alumni
                </h4>
                <div className="flex flex-wrap gap-x-8 gap-y-3 pl-8">
                  {/* Public Channels */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleChatSetting('chat_create_public_alumni', chatCreatePublicAlumni, setChatCreatePublicAlumni, 'Alumni', 'public')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                        chatCreatePublicAlumni ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatCreatePublicAlumni ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div>
                      <span className="text-sm text-cyan-800 dark:text-cyan-200">Create Public Channels</span>
                      <p className="text-xs text-cyan-600 dark:text-cyan-400">Anyone can join</p>
                    </div>
                  </div>
                  {/* Private Channels */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleChatSetting('chat_create_private_alumni', chatCreatePrivateAlumni, setChatCreatePrivateAlumni, 'Alumni', 'private')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                        chatCreatePrivateAlumni ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatCreatePrivateAlumni ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div>
                      <span className="text-sm text-cyan-800 dark:text-cyan-200">Create Private Channels</span>
                      <p className="text-xs text-cyan-600 dark:text-cyan-400">Invite only</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Students Channel Creation */}
              <div className="mb-6 pb-4 border-b border-cyan-200 dark:border-cyan-700">
                <h4 className="text-sm font-semibold text-cyan-800 dark:text-cyan-200 mb-3 flex items-center">
                  <span className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs mr-2">S</span>
                  Students
                </h4>
                <div className="flex flex-wrap gap-x-8 gap-y-3 pl-8">
                  {/* Public Channels */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleChatSetting('chat_create_public_students', chatCreatePublicStudents, setChatCreatePublicStudents, 'Students', 'public')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                        chatCreatePublicStudents ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatCreatePublicStudents ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div>
                      <span className="text-sm text-cyan-800 dark:text-cyan-200">Create Public Channels</span>
                      <p className="text-xs text-cyan-600 dark:text-cyan-400">Anyone can join</p>
                    </div>
                  </div>
                  {/* Private Channels */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleChatSetting('chat_create_private_students', chatCreatePrivateStudents, setChatCreatePrivateStudents, 'Students', 'private')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                        chatCreatePrivateStudents ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatCreatePrivateStudents ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div>
                      <span className="text-sm text-cyan-800 dark:text-cyan-200">Create Private Channels</span>
                      <p className="text-xs text-cyan-600 dark:text-cyan-400">Invite only</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recruiters Channel Creation */}
              <div className="mb-6 pb-4 border-b border-cyan-200 dark:border-cyan-700">
                <h4 className="text-sm font-semibold text-cyan-800 dark:text-cyan-200 mb-3 flex items-center">
                  <span className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-xs mr-2">R</span>
                  Recruiters
                </h4>
                <div className="flex flex-wrap gap-x-8 gap-y-3 pl-8">
                  {/* Public Channels */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleChatSetting('chat_create_public_recruiters', chatCreatePublicRecruiters, setChatCreatePublicRecruiters, 'Recruiters', 'public')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                        chatCreatePublicRecruiters ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatCreatePublicRecruiters ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div>
                      <span className="text-sm text-cyan-800 dark:text-cyan-200">Create Public Channels</span>
                      <p className="text-xs text-cyan-600 dark:text-cyan-400">Anyone can join</p>
                    </div>
                  </div>
                  {/* Private Channels */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleChatSetting('chat_create_private_recruiters', chatCreatePrivateRecruiters, setChatCreatePrivateRecruiters, 'Recruiters', 'private')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                        chatCreatePrivateRecruiters ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatCreatePrivateRecruiters ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div>
                      <span className="text-sm text-cyan-800 dark:text-cyan-200">Create Private Channels</span>
                      <p className="text-xs text-cyan-600 dark:text-cyan-400">Invite only</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Summary */}
              <div className="pt-2">
                <p className="text-xs text-cyan-600 dark:text-cyan-400 mb-2">Current Permissions:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="text-xs">
                    <span className="font-medium text-cyan-800 dark:text-cyan-200">Alumni:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`px-1.5 py-0.5 rounded ${chatCreatePublicAlumni ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        Public {chatCreatePublicAlumni ? '✓' : '✗'}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded ${chatCreatePrivateAlumni ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        Private {chatCreatePrivateAlumni ? '✓' : '✗'}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs">
                    <span className="font-medium text-cyan-800 dark:text-cyan-200">Students:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`px-1.5 py-0.5 rounded ${chatCreatePublicStudents ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        Public {chatCreatePublicStudents ? '✓' : '✗'}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded ${chatCreatePrivateStudents ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        Private {chatCreatePrivateStudents ? '✓' : '✗'}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs">
                    <span className="font-medium text-cyan-800 dark:text-cyan-200">Recruiters:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`px-1.5 py-0.5 rounded ${chatCreatePublicRecruiters ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        Public {chatCreatePublicRecruiters ? '✓' : '✗'}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded ${chatCreatePrivateRecruiters ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        Private {chatCreatePrivateRecruiters ? '✓' : '✗'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Direct Message Permissions */}
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-indigo-800 dark:text-indigo-200 mb-2">
                Direct Message Permissions
              </h3>
              <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-6">
                Control which user groups can send direct messages to each other. These settings are bidirectional (if A can message B, then B can also message A). Admins can always message anyone.
              </p>

              {/* Within Group DMs */}
              <div className="mb-6 pb-4 border-b border-indigo-200 dark:border-indigo-700">
                <h4 className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 mb-3">
                  Within Same Group
                </h4>
                <div className="flex flex-wrap gap-x-8 gap-y-3 pl-4">
                  {/* Alumni ↔ Alumni */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleDmPermission('chat_dm_alumni_alumni', chatDmAlumniAlumni, setChatDmAlumniAlumni, 'Alumni', 'Alumni')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        chatDmAlumniAlumni ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatDmAlumniAlumni ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-[10px]">A</span>
                      <span className="text-sm text-indigo-800 dark:text-indigo-200">↔</span>
                      <span className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-[10px]">A</span>
                      <span className="text-sm text-indigo-700 dark:text-indigo-300 ml-1">Alumni</span>
                    </div>
                  </div>
                  {/* Students ↔ Students */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleDmPermission('chat_dm_students_students', chatDmStudentsStudents, setChatDmStudentsStudents, 'Students', 'Students')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        chatDmStudentsStudents ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatDmStudentsStudents ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px]">S</span>
                      <span className="text-sm text-indigo-800 dark:text-indigo-200">↔</span>
                      <span className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px]">S</span>
                      <span className="text-sm text-indigo-700 dark:text-indigo-300 ml-1">Students</span>
                    </div>
                  </div>
                  {/* Recruiters ↔ Recruiters */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleDmPermission('chat_dm_recruiters_recruiters', chatDmRecruitersRecruiters, setChatDmRecruitersRecruiters, 'Recruiters', 'Recruiters')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        chatDmRecruitersRecruiters ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatDmRecruitersRecruiters ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px]">R</span>
                      <span className="text-sm text-indigo-800 dark:text-indigo-200">↔</span>
                      <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px]">R</span>
                      <span className="text-sm text-indigo-700 dark:text-indigo-300 ml-1">Recruiters</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cross-Group DMs */}
              <div className="mb-6 pb-4 border-b border-indigo-200 dark:border-indigo-700">
                <h4 className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 mb-3">
                  Between Different Groups
                </h4>
                <div className="flex flex-wrap gap-x-8 gap-y-3 pl-4">
                  {/* Alumni ↔ Students */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleDmPermission('chat_dm_alumni_students', chatDmAlumniStudents, setChatDmAlumniStudents, 'Alumni', 'Students')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        chatDmAlumniStudents ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatDmAlumniStudents ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-[10px]">A</span>
                      <span className="text-sm text-indigo-800 dark:text-indigo-200">↔</span>
                      <span className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px]">S</span>
                      <span className="text-sm text-indigo-700 dark:text-indigo-300 ml-1">Alumni ↔ Students</span>
                    </div>
                  </div>
                  {/* Alumni ↔ Recruiters */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleDmPermission('chat_dm_alumni_recruiters', chatDmAlumniRecruiters, setChatDmAlumniRecruiters, 'Alumni', 'Recruiters')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        chatDmAlumniRecruiters ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatDmAlumniRecruiters ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-[10px]">A</span>
                      <span className="text-sm text-indigo-800 dark:text-indigo-200">↔</span>
                      <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px]">R</span>
                      <span className="text-sm text-indigo-700 dark:text-indigo-300 ml-1">Alumni ↔ Recruiters</span>
                    </div>
                  </div>
                  {/* Students ↔ Recruiters */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleDmPermission('chat_dm_students_recruiters', chatDmStudentsRecruiters, setChatDmStudentsRecruiters, 'Students', 'Recruiters')}
                      disabled={settingsLoading}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        chatDmStudentsRecruiters ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatDmStudentsRecruiters ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px]">S</span>
                      <span className="text-sm text-indigo-800 dark:text-indigo-200">↔</span>
                      <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px]">R</span>
                      <span className="text-sm text-indigo-700 dark:text-indigo-300 ml-1">Students ↔ Recruiters</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* DM Status Summary */}
              <div className="pt-2">
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-2">Current DM Permissions:</p>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${chatDmAlumniAlumni ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    A↔A {chatDmAlumniAlumni ? '✓' : '✗'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${chatDmStudentsStudents ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    S↔S {chatDmStudentsStudents ? '✓' : '✗'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${chatDmRecruitersRecruiters ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    R↔R {chatDmRecruitersRecruiters ? '✓' : '✗'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${chatDmAlumniStudents ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    A↔S {chatDmAlumniStudents ? '✓' : '✗'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${chatDmAlumniRecruiters ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    A↔R {chatDmAlumniRecruiters ? '✓' : '✗'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${chatDmStudentsRecruiters ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    S↔R {chatDmStudentsRecruiters ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            </div>

            {/* Membership Management */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-amber-800 dark:text-amber-200">
                  Membership Management
                </h3>
                <button
                  onClick={() => setShowMembershipSection(!showMembershipSection)}
                  className="text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 transition-colors"
                >
                  <svg className={`w-5 h-5 transform transition-transform ${showMembershipSection ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                Manage membership tiers, feature gates, and alumni memberships. Tier checks are enforced on brokerage requests when the master toggle is enabled.
              </p>

              {/* Master Toggle */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-amber-200 dark:border-amber-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Membership Tier System</span>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {membershipTiersEnabled
                        ? 'Tier checks enforced for brokerage requests'
                        : 'All brokerage requests bypass tier checks'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={toggleMembershipTiers}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                    membershipTiersEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    membershipTiersEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {showMembershipSection && (
                <div className="space-y-6">
                  {/* Tiers Table */}
                  <div>
                    <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Membership Tiers
                    </h4>
                    {membershipTiersLoading ? (
                      <p className="text-sm text-amber-600 dark:text-amber-400">Loading tiers...</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-amber-100 dark:bg-amber-900/40">
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Level</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Category</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Price</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Status</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Enrollments</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-amber-100 dark:divide-amber-800">
                            {membershipTiers.map(tier => (
                              <tr key={tier.id} className="hover:bg-amber-50 dark:hover:bg-amber-900/30">
                                <td className="px-3 py-2 text-amber-900 dark:text-amber-100 font-medium">{tier.name}</td>
                                <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{tier.level}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    tier.tier_category === 'individual'
                                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                      : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                  }`}>
                                    {tier.tier_category}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-amber-700 dark:text-amber-300">
                                  {tier.price_cents != null ? `$${(tier.price_cents / 100).toFixed(2)}` : 'TBD'}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    tier.is_active
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                                  }`}>
                                    {tier.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{tier.active_members || 0}</td>
                                <td className="px-3 py-2">
                                  {editingTier === tier.id ? (
                                    <div className="space-y-2">
                                      <input
                                        type="number"
                                        defaultValue={tier.price_cents != null ? (tier.price_cents / 100).toFixed(2) : ''}
                                        placeholder="Price ($)"
                                        id={`tier-price-${tier.id}`}
                                        className="w-24 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                      />
                                      <input
                                        type="text"
                                        defaultValue={tier.hubspot_payment_url || ''}
                                        placeholder="HubSpot URL"
                                        id={`tier-url-${tier.id}`}
                                        className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                      />
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => {
                                            const priceEl = document.getElementById(`tier-price-${tier.id}`);
                                            const urlEl = document.getElementById(`tier-url-${tier.id}`);
                                            const updates = {};
                                            if (priceEl.value) updates.price_cents = Math.round(parseFloat(priceEl.value) * 100);
                                            if (urlEl.value !== (tier.hubspot_payment_url || '')) updates.hubspot_payment_url = urlEl.value;
                                            if (Object.keys(updates).length > 0) handleSaveTier(tier.id, updates);
                                            else setEditingTier(null);
                                          }}
                                          className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                        >Save</button>
                                        <button
                                          onClick={() => setEditingTier(null)}
                                          className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                                        >Cancel</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setEditingTier(tier.id)}
                                      className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
                                    >Edit</button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Feature Gates Editor */}
                  <div>
                    <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Feature Gates
                    </h4>
                    {featureGatesLoading ? (
                      <p className="text-sm text-amber-600 dark:text-amber-400">Loading feature gates...</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-amber-100 dark:bg-amber-900/40">
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Feature</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Min Tier</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Usage Limit</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Period</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Score Min</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Active</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-amber-100 dark:divide-amber-800">
                            {featureGates.map(gate => (
                              <tr key={gate.id} className="hover:bg-amber-50 dark:hover:bg-amber-900/30">
                                <td className="px-3 py-2 text-amber-900 dark:text-amber-100 font-medium">
                                  {gate.feature_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  <p className="text-xs text-amber-500 dark:text-amber-400 font-normal">{gate.description}</p>
                                </td>
                                <td className="px-3 py-2 text-amber-700 dark:text-amber-300">Level {gate.minimum_tier_level}</td>
                                <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{gate.usage_limit ?? 'Unlimited'}</td>
                                <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{gate.usage_period || '-'}</td>
                                <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{gate.score_minimum ?? '-'}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    gate.is_active
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                                  }`}>
                                    {gate.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  {editingGate === gate.id ? (
                                    <div className="space-y-2">
                                      <div className="flex gap-2">
                                        <input
                                          type="number"
                                          defaultValue={gate.usage_limit ?? ''}
                                          placeholder="Limit"
                                          id={`gate-limit-${gate.id}`}
                                          className="w-16 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        />
                                        <input
                                          type="number"
                                          step="0.01"
                                          defaultValue={gate.score_minimum ?? ''}
                                          placeholder="Score"
                                          id={`gate-score-${gate.id}`}
                                          className="w-16 px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        />
                                      </div>
                                      <label className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                                        <input
                                          type="checkbox"
                                          defaultChecked={gate.is_active}
                                          id={`gate-active-${gate.id}`}
                                          className="rounded"
                                        />
                                        Active
                                      </label>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => {
                                            const limitEl = document.getElementById(`gate-limit-${gate.id}`);
                                            const scoreEl = document.getElementById(`gate-score-${gate.id}`);
                                            const activeEl = document.getElementById(`gate-active-${gate.id}`);
                                            const updates = {};
                                            if (limitEl.value !== '') updates.usage_limit = parseInt(limitEl.value);
                                            if (scoreEl.value !== '') updates.score_minimum = parseFloat(scoreEl.value);
                                            updates.is_active = activeEl.checked;
                                            handleSaveGate(gate.id, updates);
                                          }}
                                          className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                        >Save</button>
                                        <button
                                          onClick={() => setEditingGate(null)}
                                          className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                                        >Cancel</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setEditingGate(gate.id)}
                                      className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
                                    >Edit</button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Grant Membership + Active Memberships */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Active Memberships ({membershipsTotal})
                      </h4>
                      <button
                        onClick={() => { setShowGrantModal(true); setGrantSelectedUser(null); setGrantSearch(''); setGrantResults([]); setGrantSelectedTier(''); setGrantStatus('comped'); setGrantExpiresAt(''); setGrantCohortYear(''); }}
                        className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Grant Membership
                      </button>
                    </div>

                    {membershipsLoading ? (
                      <p className="text-sm text-amber-600 dark:text-amber-400">Loading memberships...</p>
                    ) : memberships.length === 0 ? (
                      <p className="text-sm text-amber-600 dark:text-amber-400 italic">No active memberships yet. Use "Grant Membership" to comp a user.</p>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="bg-amber-100 dark:bg-amber-900/40">
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Alumni</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Tier</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Status</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Granted By</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Starts</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Expires</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-amber-700 dark:text-amber-300">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100 dark:divide-amber-800">
                              {memberships.map(m => (
                                <tr key={m.id} className="hover:bg-amber-50 dark:hover:bg-amber-900/30">
                                  <td className="px-3 py-2 text-amber-900 dark:text-amber-100 font-medium">
                                    {m.alumni_name || `Alumni #${m.alumni_user_id}`}
                                  </td>
                                  <td className="px-3 py-2 text-amber-700 dark:text-amber-300">{m.tier_name}</td>
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      m.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                                      m.status === 'comped' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                                      m.status === 'expired' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                                      'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                                    }`}>
                                      {m.status}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-amber-700 dark:text-amber-300 text-xs">{m.granted_by || '-'}</td>
                                  <td className="px-3 py-2 text-amber-700 dark:text-amber-300 text-xs">
                                    {m.starts_at ? new Date(m.starts_at).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-amber-700 dark:text-amber-300 text-xs">
                                    {m.expires_at ? new Date(m.expires_at).toLocaleDateString() : 'Never'}
                                  </td>
                                  <td className="px-3 py-2">
                                    {(m.status === 'active' || m.status === 'comped') && (
                                      <button
                                        onClick={() => handleRevokeMembership(m.id, m.alumni_name || `Alumni #${m.alumni_user_id}`)}
                                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                      >Revoke</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Pagination */}
                        {membershipsTotal > 20 && (
                          <div className="flex items-center justify-between mt-3">
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                              Page {membershipsPage} of {Math.ceil(membershipsTotal / 20)}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => fetchMemberships(membershipsPage - 1)}
                                disabled={membershipsPage <= 1}
                                className="px-2 py-1 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded disabled:opacity-50"
                              >Prev</button>
                              <button
                                onClick={() => fetchMemberships(membershipsPage + 1)}
                                disabled={membershipsPage >= Math.ceil(membershipsTotal / 20)}
                                className="px-2 py-1 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded disabled:opacity-50"
                              >Next</button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* LinkedIn Comments Import Settings */}
            <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-sky-800 dark:text-sky-200 mb-2">
                LinkedIn Comment Imports
              </h3>
              <p className="text-sm text-sky-700 dark:text-sky-300 mb-4">
                Control the Apify-powered LinkedIn comment scraping for executive profiles. Comments enrich profile intelligence with real engagement data.
              </p>

              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-sky-800 dark:text-sky-200">Comment Imports Enabled</span>
                    <p className="text-xs text-sky-600 dark:text-sky-400">When disabled, all import buttons are hidden and API requests are rejected</p>
                  </div>
                </div>
                <button
                  onClick={toggleLinkedInCommentsEnabled}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-7 w-14 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
                    linkedinCommentsEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    linkedinCommentsEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Monthly Budget */}
              <div className="border-t border-sky-200 dark:border-sky-700 pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-sky-800 dark:text-sky-200">Monthly Budget</span>
                    <p className="text-xs text-sky-600 dark:text-sky-400">Maximum spend on Apify comment imports per calendar month</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-sky-700 dark:text-sky-300">$</span>
                    <input
                      type="number"
                      min="0"
                      step="5"
                      value={linkedinCommentsBudget}
                      onChange={(e) => setLinkedinCommentsBudget(e.target.value)}
                      className="w-24 px-3 py-1.5 text-sm border border-sky-300 dark:border-sky-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500"
                    />
                    <button
                      onClick={() => saveLinkedInCommentsSetting('linkedin_comments_monthly_budget_usd', linkedinCommentsBudget, 'string', 'Monthly budget limit for LinkedIn comment imports (USD)')}
                      disabled={linkedinCommentsSaving}
                      className="px-3 py-1.5 text-xs bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
                    >
                      {linkedinCommentsSaving ? '...' : 'Save'}
                    </button>
                  </div>
                </div>

                {/* Default Max Pages */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-sky-800 dark:text-sky-200">Default Max Pages per Profile</span>
                    <p className="text-xs text-sky-600 dark:text-sky-400">Each page returns ~100 comments at ~$0.50/page</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={linkedinCommentsMaxPages}
                      onChange={(e) => setLinkedinCommentsMaxPages(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-sky-300 dark:border-sky-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500"
                    >
                      {[1, 2, 3, 4, 5].map(n => <option key={n} value={n.toString()}>{n} page{n > 1 ? 's' : ''} (~{n * 100} comments, ~${(n * 0.5).toFixed(2)})</option>)}
                    </select>
                    <button
                      onClick={() => saveLinkedInCommentsSetting('linkedin_comments_max_pages_per_profile', linkedinCommentsMaxPages, 'integer', 'Default max pages per profile for LinkedIn comment imports')}
                      disabled={linkedinCommentsSaving}
                      className="px-3 py-1.5 text-xs bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
                    >
                      {linkedinCommentsSaving ? '...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Status indicator */}
              <div className="mt-4 pt-4 border-t border-sky-200 dark:border-sky-700">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  linkedinCommentsEnabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  <span className={`w-2 h-2 rounded-full mr-2 ${linkedinCommentsEnabled ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  {linkedinCommentsEnabled ? `Imports Enabled — Budget: $${linkedinCommentsBudget}/mo, Default: ${linkedinCommentsMaxPages} page(s)` : 'Comment Imports Disabled'}
                </span>
              </div>
            </div>

            {/* Versium/DataFinder Predictive Scores Settings */}
            <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-violet-800 dark:text-violet-200">
                    Versium Predictive Scores
                  </h3>
                  <p className="text-sm text-violet-600 dark:text-violet-400 mt-1">
                    DataFinder integration for Giving Score and Wealth Score enrichment on executive contacts. Each lookup uses API credits.
                  </p>
                </div>
                <button
                  onClick={toggleVersiumEnabled}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    versiumEnabled ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    versiumEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* API Key */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-violet-700 dark:text-violet-300 mb-1">API Key</label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={versiumApiKey}
                    onChange={(e) => setVersiumApiKey(e.target.value)}
                    placeholder="Enter Versium API key..."
                    className="flex-1 px-3 py-2 border border-violet-300 dark:border-violet-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveVersiumApiKey}
                    disabled={versiumSaving}
                    className="px-4 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 disabled:opacity-50"
                  >
                    {versiumSaving ? '...' : 'Save'}
                  </button>
                </div>
                {versiumApiKey && (
                  <p className="mt-1 text-xs text-violet-500 dark:text-violet-400">
                    Key set (ending ...{versiumApiKey.slice(-4)})
                  </p>
                )}
              </div>

              {/* Status indicator */}
              <div className="mt-4 pt-4 border-t border-violet-200 dark:border-violet-700">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  versiumEnabled && versiumApiKey
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : versiumEnabled && !versiumApiKey
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  <span className={`w-2 h-2 rounded-full mr-2 ${
                    versiumEnabled && versiumApiKey ? 'bg-green-500' : versiumEnabled && !versiumApiKey ? 'bg-yellow-500' : 'bg-red-500'
                  }`}></span>
                  {versiumEnabled && versiumApiKey ? 'Versium Enabled — Ready' : versiumEnabled && !versiumApiKey ? 'Enabled — API Key Required' : 'Versium Disabled'}
                </span>
              </div>
            </div>

            {/* Auto-Process LinkedIn Profiles Setting */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                    Auto-Process LinkedIn Profiles
                  </h3>
                  <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                    When enabled, saving a LinkedIn URL on an unprovisioned contact automatically scrapes the profile via Apify,
                    creates an executive record, and runs Bedrock leadership scoring. Contacts scoring 7+ also receive DataFinder
                    predictive scores automatically.
                  </p>
                </div>
                <button
                  onClick={toggleAutoProcessLinkedIn}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoProcessLinkedIn ? 'bg-amber-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoProcessLinkedIn ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Status indicator */}
              <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-700">
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    autoProcessLinkedIn
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-full mr-2 ${autoProcessLinkedIn ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                    {autoProcessLinkedIn ? 'Auto-Processing Enabled' : 'Auto-Processing Disabled'}
                  </span>
                  <span className="text-xs text-amber-500 dark:text-amber-400">~$0.08 per profile (Apify + Bedrock)</span>
                </div>
              </div>
            </div>

            {/* Internship Matching & Student Resumes Settings */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                    Internship Matching & Student Resumes
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    Fetches internship postings from the Fantastic Jobs Internships API (~500/week) and matches them to student resumes
                    using semantic embeddings (Amazon Titan V2, 1024-dim cosine similarity).
                  </p>
                </div>
              </div>

              {/* Toggle: Internship Matching */}
              <div className="flex items-center justify-between py-3 border-b border-green-200 dark:border-green-700">
                <div>
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">Internship Matching</span>
                  <p className="text-xs text-green-500 dark:text-green-400">Enable internship matching pipeline and student dashboard section</p>
                </div>
                <button
                  onClick={toggleInternshipMatching}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    internshipMatchingEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    internshipMatchingEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Toggle: Student Resume Upload */}
              <div className="flex items-center justify-between py-3 border-b border-green-200 dark:border-green-700">
                <div>
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">Student Resume Upload</span>
                  <p className="text-xs text-green-500 dark:text-green-400">Enable resume upload widget on student dashboard</p>
                </div>
                <button
                  onClick={toggleStudentResumeUpload}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    studentResumeUploadEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    studentResumeUploadEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* API Key + Threshold */}
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-green-700 dark:text-green-300 mb-1">RapidAPI Key (Internships API)</label>
                  <input
                    type="password"
                    value={internshipApiKey}
                    onChange={(e) => setInternshipApiKey(e.target.value)}
                    placeholder="Enter RapidAPI key..."
                    className="w-full px-3 py-2 border border-green-300 dark:border-green-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-700 dark:text-green-300 mb-1">Similarity Threshold (0-1)</label>
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    value={internshipSimilarityThreshold}
                    onChange={(e) => setInternshipSimilarityThreshold(e.target.value)}
                    className="w-32 px-3 py-2 border border-green-300 dark:border-green-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <button
                  onClick={saveInternshipSettings}
                  disabled={internshipSaving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  {internshipSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-700 flex gap-3 flex-wrap">
                <button
                  onClick={triggerInternshipFetch}
                  disabled={internshipFetching || !internshipApiKey}
                  className="px-4 py-2 bg-green-700 text-white rounded-lg text-sm hover:bg-green-800 disabled:opacity-50"
                >
                  {internshipFetching ? 'Fetching...' : 'Fetch Internships Now'}
                </button>
                <button
                  onClick={triggerResumeBackfill}
                  disabled={internshipBackfilling}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  {internshipBackfilling ? 'Processing...' : 'Backfill Student Resumes'}
                </button>
                <button
                  onClick={triggerComputeMatches}
                  disabled={internshipMatching}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50"
                >
                  {internshipMatching ? 'Computing...' : 'Compute Matches Now'}
                </button>
                <a
                  href="/admin/internship-postings"
                  className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 inline-flex items-center gap-1"
                >
                  View Internship Postings &rarr;
                </a>
              </div>

              {/* Stats Display */}
              {internshipStats && (
                <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-700">
                  <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-green-200 dark:border-green-700">
                      <div className="text-lg font-bold text-green-700 dark:text-green-300">{internshipStats.active_internships}</div>
                      <div className="text-xs text-green-500">Active Internships</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-green-200 dark:border-green-700">
                      <div className="text-lg font-bold text-green-700 dark:text-green-300">{internshipStats.internships_with_embeddings}</div>
                      <div className="text-xs text-green-500">With Embeddings</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-green-200 dark:border-green-700">
                      <div className="text-lg font-bold text-green-700 dark:text-green-300">{internshipStats.active_resumes}</div>
                      <div className="text-xs text-green-500">Active Resumes</div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-green-200 dark:border-green-700">
                      <div className="text-lg font-bold text-green-700 dark:text-green-300">{internshipStats.total_matches}</div>
                      <div className="text-xs text-green-500">Total Matches</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SWEE (Strategic Workforce Evolution Engine) Settings */}
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-200">
                    SWEE — Strategic Workforce Evolution Engine
                  </h3>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                    Analyzes earnings call transcripts to detect corporate workforce strategy shifts using 6 strategy anchors
                    and Chain-of-Truth AI scoring via AWS Bedrock.
                  </p>
                </div>
                <button
                  onClick={toggleSweeEnabled}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    sweeEnabled ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    sweeEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* EarningsCall API Key */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-1">EarningsCall API Key</label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={sweeApiKey}
                    onChange={(e) => setSweeApiKey(e.target.value)}
                    placeholder="Enter EarningsCall.biz API key..."
                    className="flex-1 px-3 py-2 border border-emerald-300 dark:border-emerald-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveSweeApiKey}
                    disabled={sweeSaving}
                    className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                </div>
                {sweeApiKey && (
                  <p className="mt-1 text-xs text-emerald-500 dark:text-emerald-400">
                    Key set (ending ...{sweeApiKey.slice(-4)})
                  </p>
                )}
              </div>

              {/* FMP (Financial Modeling Prep) API Key */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-1">FMP API Key (Employee Count Data)</label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={sweeFmpApiKey}
                    onChange={(e) => setSweeFmpApiKey(e.target.value)}
                    placeholder="Enter Financial Modeling Prep API key..."
                    className="flex-1 px-3 py-2 border border-emerald-300 dark:border-emerald-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveFmpApiKey}
                    disabled={sweeSaving}
                    className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                </div>
                {sweeFmpApiKey && (
                  <p className="mt-1 text-xs text-emerald-500 dark:text-emerald-400">
                    Key set (ending ...{sweeFmpApiKey.slice(-4)})
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Used for SEC filing employee count data (financialmodelingprep.com)
                </p>
              </div>

              {/* TheirStack API Key (Hiring Velocity Tracker) */}
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 dark:text-blue-300">
                      TheirStack — Hiring Velocity Tracker
                    </label>
                    <p className="text-xs text-blue-500 dark:text-blue-400 mt-0.5">
                      Real-time job posting data from 315K+ sources. Tracks hiring velocity, seniority mix, and technology signals. $59/mo starter plan, 1,500 credits.
                    </p>
                  </div>
                  <button
                    onClick={toggleTheirStackEnabled}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      theirStackEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      theirStackEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={theirStackApiKey}
                    onChange={(e) => setTheirStackApiKey(e.target.value)}
                    placeholder="Enter TheirStack API key..."
                    className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveTheirStackApiKey}
                    disabled={sweeSaving}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                </div>
                {theirStackApiKey && (
                  <p className="mt-1 text-xs text-blue-500 dark:text-blue-400">
                    Key set (ending ...{theirStackApiKey.slice(-4)})
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Monthly snapshots collect 25 job records per tracked company (25 credits each). Enable tracking per-company on the SWEE Companies tab.
                </p>
              </div>

              {/* Similarity Threshold */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-1">
                  Cosine Similarity Threshold
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    step="0.05"
                    min="0.1"
                    max="1.0"
                    value={sweeSimilarityThreshold}
                    onChange={(e) => setSweeSimilarityThreshold(e.target.value)}
                    className="w-24 px-3 py-2 border border-emerald-300 dark:border-emerald-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveSweeSimilarityThreshold}
                    disabled={sweeSaving}
                    className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                  <span className="text-xs text-emerald-500 dark:text-emerald-400">
                    Chunks scoring below this are excluded from AI scoring (fallback: 0.30). Higher = stricter, lower = more chunks scored.
                  </span>
                </div>
              </div>

              {/* Daily Scan Toggle */}
              <div className="flex items-center justify-between mb-4 p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Daily Transcript Scan</span>
                  <p className="text-xs text-emerald-500 dark:text-emerald-400 mt-0.5">
                    Automatically fetches and scores new transcripts nightly (8 PM ET)
                  </p>
                </div>
                <button
                  onClick={toggleSweeDailyScan}
                  disabled={settingsLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    sweeDailyScanEnabled ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-600'
                  } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    sweeDailyScanEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* External Signals (WARN Act + SEC 8-K) */}
              <div className="mb-4 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                <h4 className="text-sm font-semibold text-orange-700 dark:text-orange-300 mb-3">
                  External Signals — WARN Act + SEC EDGAR
                </h4>
                <p className="text-xs text-orange-500 dark:text-orange-400 mb-3">
                  Automatically scans for WARN Act layoff notices and SEC 8-K filings (restructuring, executive changes) for tracked companies.
                </p>

                {/* WARN Pipeline Toggle + API Key */}
                <div className="flex items-center justify-between mb-3 p-2 bg-orange-100/50 dark:bg-orange-900/30 rounded">
                  <div>
                    <span className="text-sm font-medium text-orange-700 dark:text-orange-300">WARN Act Pipeline</span>
                    <p className="text-xs text-orange-500 dark:text-orange-400 mt-0.5">
                      Daily scan via WARN Firehose API (free tier: 25 calls/day, 25 records/call, last 90 days)
                    </p>
                  </div>
                  <button
                    onClick={toggleWarnEnabled}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      warnEnabled ? 'bg-orange-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      warnEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="password"
                    value={warnApiKey}
                    onChange={(e) => setWarnApiKey(e.target.value)}
                    placeholder="Enter WARN Firehose API key..."
                    className="flex-1 px-3 py-2 border border-orange-300 dark:border-orange-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveWarnApiKey}
                    disabled={sweeSaving}
                    className="px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                </div>
                {warnApiKey && (
                  <p className="mb-3 text-xs text-orange-500">Key set (ending ...{warnApiKey.slice(-4)})</p>
                )}

                {/* SEC EDGAR Toggle + User-Agent */}
                <div className="flex items-center justify-between mb-3 p-2 bg-blue-100/50 dark:bg-blue-900/30 rounded">
                  <div>
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">SEC EDGAR Pipeline</span>
                    <p className="text-xs text-blue-500 dark:text-blue-400 mt-0.5">
                      Daily 8-K filing scan (free, no API key — requires User-Agent header)
                    </p>
                  </div>
                  <button
                    onClick={toggleSecEdgarEnabled}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      secEdgarEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      secEdgarEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={secEdgarUserAgent}
                    onChange={(e) => setSecEdgarUserAgent(e.target.value)}
                    placeholder="Company Name admin@email.com"
                    className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveSecEdgarUserAgent}
                    disabled={sweeSaving}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  SEC requires a User-Agent header identifying your organization and contact email (e.g., "NACME Platform admin@nacme.org")
                </p>
              </div>

              {/* Workforce Intelligence (WI) Module */}
              <div className="mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Workforce Intelligence Module</span>
                    <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">
                      Classifies chunks into 7 workforce categories (sizing, retention, compensation, etc.) during pipeline Stage 5
                    </p>
                  </div>
                  <button
                    onClick={toggleWiEnabled}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      wiEnabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      wiEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-xs font-medium text-indigo-600 dark:text-indigo-400">WI Gate Threshold:</label>
                  <input
                    type="number"
                    step="0.05"
                    min="0.1"
                    max="1.0"
                    value={wiSimilarityThreshold}
                    onChange={(e) => setWiSimilarityThreshold(e.target.value)}
                    className="w-20 px-2 py-1 border border-indigo-300 dark:border-indigo-700 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveWiSimilarityThreshold}
                    disabled={sweeSaving}
                    className="px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                  <span className="text-xs text-indigo-400">Chunks below this cosine score skip WI scoring</span>
                </div>
              </div>

              {/* FMP News & Press Releases Pipeline */}
              <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">FMP News & Press Releases Pipeline</span>
                    <p className="text-xs text-emerald-500 dark:text-emerald-400 mt-0.5">
                      Scans FMP stock news and company press releases for workforce-relevant signals. Runs daily at 8 AM ET.
                    </p>
                  </div>
                  <button
                    onClick={toggleFmpNewsPipeline}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      fmpNewsPipelineEnabled ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      fmpNewsPipelineEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Layoffs.fyi Pipeline */}
              <div className="mb-4 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg">
                <h4 className="text-sm font-semibold text-rose-700 dark:text-rose-300 mb-3">
                  Layoffs.fyi Pipeline
                </h4>
                <p className="text-xs text-rose-500 dark:text-rose-400 mb-3">
                  Scrapes Layoffs.fyi Airtable embed via Apify for layoff announcements. Cross-validates with WARN Act data. Supports CSV backfill from Kaggle dataset.
                </p>

                {/* Enabled Toggle */}
                <div className="flex items-center justify-between mb-3 p-2 bg-rose-100/50 dark:bg-rose-900/30 rounded">
                  <div>
                    <span className="text-sm font-medium text-rose-700 dark:text-rose-300">Pipeline Enabled</span>
                    <p className="text-xs text-rose-500 dark:text-rose-400 mt-0.5">
                      Enable live scanning via Apify (spencers/airtable-scraper actor)
                    </p>
                  </div>
                  <button
                    onClick={toggleLayoffsFyiEnabled}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      layoffsFyiEnabled ? 'bg-rose-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      layoffsFyiEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Lookback Days */}
                <div className="flex gap-2 items-center mb-3">
                  <label className="text-xs font-medium text-rose-600 dark:text-rose-400">Lookback Days:</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={layoffsFyiLookbackDays}
                    onChange={(e) => setLayoffsFyiLookbackDays(e.target.value)}
                    className="w-20 px-2 py-1 border border-rose-300 dark:border-rose-700 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveLayoffsFyiLookbackDays}
                    disabled={sweeSaving}
                    className="px-3 py-1 bg-rose-600 text-white text-xs rounded hover:bg-rose-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                  <span className="text-xs text-rose-400">How far back to scan (default 14)</span>
                </div>

                {/* Include International Toggle */}
                <div className="flex items-center justify-between mb-3 p-2 bg-rose-100/50 dark:bg-rose-900/30 rounded">
                  <div>
                    <span className="text-sm font-medium text-rose-700 dark:text-rose-300">Include International</span>
                    <p className="text-xs text-rose-500 dark:text-rose-400 mt-0.5">
                      Process layoff signals from non-US countries (demoted one urgency tier)
                    </p>
                  </div>
                  <button
                    onClick={toggleLayoffsFyiInternational}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      layoffsFyiIncludeInternational ? 'bg-rose-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      layoffsFyiIncludeInternational ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* International Weight */}
                <div className="flex gap-2 items-center">
                  <label className="text-xs font-medium text-rose-600 dark:text-rose-400">Intl Weight:</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.0"
                    max="1.0"
                    value={layoffsFyiInternationalWeight}
                    onChange={(e) => setLayoffsFyiInternationalWeight(e.target.value)}
                    className="w-20 px-2 py-1 border border-rose-300 dark:border-rose-700 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveLayoffsFyiInternationalWeight}
                    disabled={sweeSaving}
                    className="px-3 py-1 bg-rose-600 text-white text-xs rounded hover:bg-rose-700 disabled:opacity-50"
                  >
                    {sweeSaving ? '...' : 'Save'}
                  </button>
                  <span className="text-xs text-rose-400">Urgency multiplier for intl signals (0.0-1.0)</span>
                </div>
              </div>

              {/* LinkedIn Company Insights */}
              <div className="mb-4 p-4 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg">
                <h4 className="text-sm font-semibold text-sky-700 dark:text-sky-300 mb-3">
                  LinkedIn Company Insights
                </h4>
                <p className="text-xs text-sky-500 dark:text-sky-400 mb-3">
                  Fetches headcount trends, hiring breakdown, median tenure, and job openings via RICEMAN Apify actor. ~$0.19 per company lookup. No LinkedIn cookie required.
                </p>

                {/* Pipeline Enabled Toggle */}
                <div className="flex items-center justify-between mb-3 p-2 bg-sky-100/50 dark:bg-sky-900/30 rounded">
                  <div>
                    <span className="text-sm font-medium text-sky-700 dark:text-sky-300">Pipeline Enabled</span>
                    <p className="text-xs text-sky-500 dark:text-sky-400 mt-0.5">
                      Enable on-demand LinkedIn company insight lookups
                    </p>
                  </div>
                  <button
                    onClick={toggleLinkedinInsightsEnabled}
                    disabled={settingsLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      linkedinInsightsEnabled ? 'bg-sky-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      linkedinInsightsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Monthly Refresh Toggle */}
                <div className="flex items-center justify-between p-2 bg-sky-100/50 dark:bg-sky-900/30 rounded">
                  <div>
                    <span className="text-sm font-medium text-sky-700 dark:text-sky-300">Monthly Refresh</span>
                    <p className="text-xs text-sky-500 dark:text-sky-400 mt-0.5">
                      Auto-refresh tracked companies monthly (~$0.19/company). Turn off to contain costs.
                    </p>
                  </div>
                  <button
                    onClick={toggleLinkedinInsightsMonthlyRefresh}
                    disabled={settingsLoading || !linkedinInsightsEnabled}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      linkedinInsightsMonthlyRefresh && linkedinInsightsEnabled ? 'bg-sky-600' : 'bg-gray-300 dark:bg-gray-600'
                    } ${settingsLoading || !linkedinInsightsEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      linkedinInsightsMonthlyRefresh && linkedinInsightsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Status indicator */}
              <div className="mt-4 pt-4 border-t border-emerald-200 dark:border-emerald-700">
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    sweeEnabled && sweeApiKey
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : sweeEnabled && !sweeApiKey
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      sweeEnabled && sweeApiKey ? 'bg-green-500' : sweeEnabled && !sweeApiKey ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></span>
                    {sweeEnabled && sweeApiKey
                      ? `SWEE Active — Threshold: ${sweeSimilarityThreshold}, Scan: ${sweeDailyScanEnabled ? 'On' : 'Off'}${wiEnabled ? ', WI: On' : ''}${warnEnabled ? ', WARN: On' : ''}${secEdgarEnabled ? ', 8-K: On' : ''}${fmpNewsPipelineEnabled ? ', FMP News: On' : ''}${layoffsFyiEnabled ? ', Layoffs.fyi: On' : ''}${linkedinInsightsEnabled ? ', LI Insights: On' : ''}`
                      : sweeEnabled && !sweeApiKey
                      ? 'Enabled — API Key Required'
                      : 'SWEE Disabled'}
                  </span>
                  <span className="text-xs text-emerald-500 dark:text-emerald-400">~$0.04 per transcript (Bedrock + embeddings)</span>
                </div>
              </div>
            </div>

            {/* Grant Membership Modal */}
            {showGrantModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Grant Membership</h3>
                    <button onClick={() => setShowGrantModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Alumni Search */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search Alumni</label>
                    <input
                      type="text"
                      value={grantSearch}
                      onChange={(e) => { setGrantSearch(e.target.value); handleSearchAlumni(e.target.value); }}
                      placeholder="Name or email..."
                      className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {grantResults.length > 0 && !grantSelectedUser && (
                      <div className="mt-1 max-h-40 overflow-y-auto border rounded-lg dark:border-gray-600">
                        {grantResults.map(user => (
                          <button
                            key={user.id}
                            onClick={() => { setGrantSelectedUser(user); setGrantSearch(`${user.first_name} ${user.last_name}`); setGrantResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                          >
                            {user.first_name} {user.last_name} {user.current_employer ? <span className="text-xs text-gray-500">({user.current_employer})</span> : user.university ? <span className="text-xs text-gray-500">({user.university})</span> : <span className="text-xs text-gray-500">(ID: {user.id})</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {grantSelectedUser && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 text-xs rounded-full">
                          {grantSelectedUser.first_name} {grantSelectedUser.last_name}
                        </span>
                        <button onClick={() => { setGrantSelectedUser(null); setGrantSearch(''); }} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                      </div>
                    )}
                  </div>

                  {/* Tier Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tier</label>
                    <select
                      value={grantSelectedTier}
                      onChange={(e) => setGrantSelectedTier(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="">Select tier...</option>
                      {membershipTiers.filter(t => t.is_active).map(t => (
                        <option key={t.slug} value={t.slug}>{t.name} (Level {t.level})</option>
                      ))}
                    </select>
                  </div>

                  {/* Status */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <select
                      value={grantStatus}
                      onChange={(e) => setGrantStatus(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="comped">Comped (Free)</option>
                      <option value="active">Active (Paid)</option>
                    </select>
                  </div>

                  {/* Expires At */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expires (optional)</label>
                    <input
                      type="date"
                      value={grantExpiresAt}
                      onChange={(e) => setGrantExpiresAt(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  {/* Cohort Year (for Fellows) */}
                  {grantSelectedTier === 'fellow' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cohort Year</label>
                      <input
                        type="number"
                        value={grantCohortYear}
                        onChange={(e) => setGrantCohortYear(e.target.value)}
                        placeholder={new Date().getFullYear().toString()}
                        className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    onClick={handleGrantMembership}
                    disabled={!grantSelectedUser || !grantSelectedTier || grantLoading}
                    className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {grantLoading ? 'Granting...' : 'Grant Membership'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Settings Modal */}
        {showSettingsModal && settingsModalData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center mb-4">
                {settingsModalData.success ? (
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                <h3 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">
                  {settingsModalData.success ? 'Success' : 'Error'}
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {settingsModalData.message}
              </p>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminRouteGuard>
  );
};

export default AdminPlatformSettings;
